import { clearVerifiedSession } from '../services/userAccessService';
import { normalizeUserSession } from '../services/userSession';
import { useState, useCallback } from 'react';
import { userVerificationService } from '../services/userVerificationService';
import { API_ERROR_TYPES, ApiError } from '../services/apiError';

export const USER_SESSION_STORAGE_KEY = 'pecuaria_user_session';
export const USER_SESSION_DURATION = 24 * 60 * 60 * 1000;

const hasSessionIdentity = (sessionData) => Boolean(
  sessionData?.email || sessionData?.user?.email
);

export const isValidLocalSession = (sessionData, currentTime = Date.now()) => {
  if (!sessionData || typeof sessionData !== 'object' || Array.isArray(sessionData)) {
    return false;
  }

  if (!hasSessionIdentity(sessionData)) {
    return false;
  }

  return Number.isFinite(sessionData.expiresAt) && sessionData.expiresAt > currentTime;
};

export const useUserAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Salvar sessão no localStorage
  const saveUserSession = useCallback((userData) => {
    try {
      const sessionData = {
        ...normalizeUserSession(userData),
        cachedAt: Date.now(),
        expiresAt: Date.now() + USER_SESSION_DURATION
      };
      localStorage.setItem(USER_SESSION_STORAGE_KEY, JSON.stringify(sessionData));
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  // Verificar se usuário existe (verificação rápida)
  const checkUserExists = useCallback(async (email) => {
    if (!email || !email.trim()) return false;
    
    // Validar formato do email antes de fazer requisição
    if (!userVerificationService.isValidEmail(email.trim())) {
      const validationError = new ApiError({
        type: API_ERROR_TYPES.VALIDATION_ERROR,
        code: 'INVALID_EMAIL',
        message: 'Formato de email inválido',
        operation: 'user.check-exists',
      });
      setError(validationError.message);
      throw validationError;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const exists = await userVerificationService.checkUserExists(email.trim());
      return exists;
    } catch (err) {
      setError(err.message || 'Erro ao verificar usuário');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Buscar dados completos do usuário
  const fetchUserData = useCallback(async (email) => {
    if (!email || !email.trim()) return null;
    
    // Validar formato do email antes de fazer requisição
    if (!userVerificationService.isValidEmail(email.trim())) {
      const validationError = new ApiError({
        type: API_ERROR_TYPES.VALIDATION_ERROR,
        code: 'INVALID_EMAIL',
        message: 'Formato de email inválido',
        operation: 'user.load',
      });
      setError(validationError.message);
      throw validationError;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const userData = await userVerificationService.getUserData(email.trim());
      
      if (userData) {
        // Salvar dados na sessão local
        saveUserSession(userData);
      }
      
      return userData;
    } catch (err) {
      setError(err.message || 'Erro ao buscar dados do usuário');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [saveUserSession]);

  // Verificar sessão local (cache)
  const getLocalSession = useCallback(() => {
    try {
      const stored = localStorage.getItem(USER_SESSION_STORAGE_KEY);
      if (!stored) return null;
      
      const sessionData = JSON.parse(stored);
      if (!isValidLocalSession(sessionData)) {
        localStorage.removeItem(USER_SESSION_STORAGE_KEY);
        return null;
      }
      return { ...sessionData, ...normalizeUserSession(sessionData) };
    } catch (error) {
      localStorage.removeItem(USER_SESSION_STORAGE_KEY);
      return null;
    }
  }, []);

  // Limpar sessão
  const clearSession = useCallback(() => {
    try {
      localStorage.removeItem(USER_SESSION_STORAGE_KEY);
      clearVerifiedSession();
      window.dispatchEvent(new Event('mybeef-session-cleared'));
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  // Verificar se é usuário retornante
  const isReturningUser = useCallback(() => {
    return getLocalSession() !== null;
  }, [getLocalSession]);

  // Atualizar dados da sessão local
  const updateLocalSession = useCallback((updates) => {
    try {
      const currentSession = getLocalSession();
      if (currentSession) {
        const updatedSession = {
          ...currentSession,
          ...updates,
          cachedAt: Date.now() // Atualizar timestamp
        };
        localStorage.setItem(USER_SESSION_STORAGE_KEY, JSON.stringify(updatedSession));
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [getLocalSession]);

  // Verificar se cache está expirado
  const isCacheExpired = useCallback(() => {
    const session = getLocalSession();
    if (!session) return true;
    
    return Date.now() > session.expiresAt;
  }, [getLocalSession]);

  return {
    loading,
    error,
    checkUserExists,
    fetchUserData,
    getLocalSession,
    saveUserSession,
    updateLocalSession,
    clearSession,
    isReturningUser,
    isCacheExpired,
    setError // Para limpar erro manualmente
  };
};

export default useUserAuth;
